import Avo from '@susu/custom-made/Avo';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions } from '@susu/custom-made/providers/reducers/option';
import { Clients } from '@susu/custom-made/utils/constants';
import type { Monogram, MonogramPositions, Option } from '@susu/custom-made/utils/interfaces';
import { getAvoEventParamsWithOption } from '@susu/custom-made/utils/utils';
import { useContext, useEffect, useState } from 'react';

import { ButtonType } from '../button/button';
import Modal from '../modal/modal';
import { MonogramDefaultPanel } from './monogramDefaultPanel/monogramDefaultPanel';
import { MonogramDetailsPanel } from './monogramDetailsPanel/monogramDetailsPanel';

export const MonogramPanel = ({
  isMonogramShown,
  options,
  configurationTypeId,
}: {
  isMonogramShown: boolean;
  options: Option[];
  configurationTypeId: string;
}) => {
  const [isMonogramDetailsShown, setIsMonogramDetailsShown] = useState<boolean>(false);
  const [monogramData, setMonogramData] = useState<Option>();
  const [monogramObj, setMonogramObj] = useState<Monogram>({});
  const [selectedMonogramId, setSelectedMonogramId] = useState<number>(0);
  const [isDeleteMonogramModalOpen, setIsDeleteMonogramModalOpen] = useState<boolean>(false);
  const [deleteMonogramItem, setDeleteMonogramItem] = useState<MonogramPositions | null>(null);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const { state, dispatch } = useContext(ThemeContext);
  const { removeSelectedMonogramFromMissingValues } = useMissingValues();

  const applyMonogram = () => {
    dispatch({
      type: actions.ADD_MONOGRAM,
      payload: monogramObj,
    });

    if (configurationTypeId) {
      removeSelectedMonogramFromMissingValues(configurationTypeId, +Object.keys(monogramObj)[0]);
    }
  };

  const deleteMonogram = (position: MonogramPositions) => {
    setDeleteMonogramItem(position);
    setIsDeleteMonogramModalOpen(true);
    state.client === Clients.onLine && Avo.monogramDelete(getAvoEventParamsWithOption(state, position.name));
  };

  const hideMonogramModal = () => {
    setDeleteMonogramItem(null);
    setIsDeleteMonogramModalOpen(false);
  };

  const confirmDeleteMonogram = () => {
    if (deleteMonogramItem) {
      state.client === Clients.onLine &&
        Avo.monogramDeleteConfirm(getAvoEventParamsWithOption(state, deleteMonogramItem.name));
      dispatch({
        type: actions.DELETE_MONOGRAM,
        payload: deleteMonogramItem.id,
      });

      hideMonogramModal();

      removeSelectedMonogramFromMissingValues(configurationTypeId, deleteMonogramItem.id);
    }
  };

  const closeDeleteMonogramModal = () => {
    if (deleteMonogramItem) {
      state.client === Clients.onLine &&
        Avo.monogramDeleteCancel(getAvoEventParamsWithOption(state, deleteMonogramItem.name));
    }
    hideMonogramModal();
  };

  const editMonogram = (position: MonogramPositions) => {
    const tmpMonogramObj = {
      [position.id]: { ...state.selectedMonogram[position.id] },
    };
    setMonogramObj(tmpMonogramObj);
    setIsMonogramDetailsShown(true);
    setSelectedMonogramId(position.id);
    state.client === Clients.onLine && Avo.monogramChange(getAvoEventParamsWithOption(state, position.name));
  };

  const openMonogramDetails = (positionId: number) => {
    const hiddenPositions = state.hiddenOptionsAndValues?.monogramPositions.length ?? 0;
    const selectedPositions = Object.keys(state.selectedMonogram).length;
    if (
      monogramData?.maxAllowedNumberOfPositions &&
      monogramData?.maxAllowedNumberOfPositions <= selectedPositions - hiddenPositions
    ) {
      setIsWarningModalOpen(true);
    } else {
      setIsMonogramDetailsShown(true);
      setSelectedMonogramId(positionId);
    }
  };

  const closeWarningModal = () => {
    setIsWarningModalOpen(false);
  };

  const allowedMonogramText = `Only ${
    monogramData?.maxAllowedNumberOfPositions === 1 ? 'one' : monogramData?.maxAllowedNumberOfPositions
  } monogram ${
    monogramData?.maxAllowedNumberOfPositions === 1 ? 'position is' : 'positions are'
  } allowed. Please delete your current ${
    monogramData?.maxAllowedNumberOfPositions === 1 ? 'monogram' : 'monograms'
  } first.`;

  useEffect(() => {
    if (options) {
      options.forEach(option => {
        if (option.type === 'MONOGRAM') {
          setMonogramData(option);
        }
      });
    }
  }, [options]);

  return (
    <>
      <MonogramDefaultPanel
        monogramData={monogramData}
        isMonogramShown={isMonogramShown}
        isDeleteMonogramModalOpen={isDeleteMonogramModalOpen}
        closeDeleteMonogramModal={closeDeleteMonogramModal}
        openMonogramDetails={openMonogramDetails}
        editMonogram={editMonogram}
        deleteMonogram={deleteMonogram}
        confirmDeleteMonogram={confirmDeleteMonogram}
        deleteMonogramItem={deleteMonogramItem}
      />
      <MonogramDetailsPanel
        monogramData={monogramData}
        monogramObj={monogramObj}
        selectedMonogramId={selectedMonogramId}
        isMonogramDetailsShown={isMonogramShown && isMonogramDetailsShown}
        setIsMonogramDetailsShown={setIsMonogramDetailsShown}
        setMonogramObj={setMonogramObj}
        applyMonogram={applyMonogram}
      />
      <Modal
        isOpen={isWarningModalOpen}
        description={allowedMonogramText}
        firstButtonText="Cancel"
        firstButtonType={ButtonType.SecondaryAlert}
        onFirstButtonClick={closeWarningModal}
        onOverlayClick={closeWarningModal}
        isFullSize={true}
        testId="monogram-allowed-number-modal"
      />
    </>
  );
};
